import React from 'react';
import styled from 'styled-components';
import CKEditor from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { FormLabel } from '@material-ui/core';

const EditorWrapper = styled.div<{ height?: number }>`
  .ck-editor__editable {
    min-height: ${(props) => props.height || 200}px !important;
  }
`;

interface RichTextEditorProps {
  value?: string;
  onChange: (value: string) => void;
  label?: string;
  helperText?: string;
  height?: number;
  error?: boolean;
  errorMessage?: string;
  disabled?: boolean;
  onTouch?: () => void; // Optional callback for when field is touched (redux-form compatibility)
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value = '',
  onChange,
  label,
  helperText,
  height = 200,
  error = false,
  errorMessage,
  disabled = false,
  onTouch,
}) => {
  const handleEditorChange = (event: any, editor: any) => {
    const data = editor.getData();

    // This condition will be true when the component is initially rendered.
    // We don't want to call onChange(null) here, because then the field
    // will be 'touched', so we just do nothing
    if (!value && data === '<p>&nbsp;</p>') {
      return;
    }

    // Without this, the required check doesn't work. And we also don't want to save this empty <p>
    if (data === '<p>&nbsp;</p>') {
      onChange('');
      if (onTouch) onTouch();
      return;
    }

    if (typeof onChange === 'function') {
      onChange(data);
      if (onTouch) onTouch();
    }
  };

  return (
    <EditorWrapper height={height}>
      {label && (
        <div style={{ marginBottom: 10 }}>
          <FormLabel style={{ fontSize: 11, textTransform: 'uppercase' }}>{label}</FormLabel>
        </div>
      )}
      <CKEditor
        editor={ClassicEditor}
        config={{
          // Note that content from CKE5 is also sanitized server-side based on a white-list
          // so it would need to be adjusted if new plugins are added
          removePlugins: [
            'BlockQuote',
            'MediaEmbed',
            'CKFinder',
            'Table',
            'TableToolbar',
            'ImageUpload',
          ],
        }}
        data={value}
        onChange={handleEditorChange}
        disabled={disabled}
      />
      {helperText && (
        <div style={{ marginTop: 10 }}>
          <FormLabel style={{ fontSize: 12 }}>{helperText}</FormLabel>
        </div>
      )}
      {error && errorMessage && (
        <div>
          <FormLabel error style={{ fontSize: 11, marginTop: 10 }}>
            {errorMessage}
          </FormLabel>
        </div>
      )}
    </EditorWrapper>
  );
};

export default RichTextEditor;
